import {
  PracticeTemplateDetail,
  ProgramBenchmarkName,
  ProgramTask,
  TaskDataTraitStatus,
} from '@cibo/core'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import PeopleIcon from '@mui/icons-material/People'
import PersonIcon from '@mui/icons-material/Person'
import {
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import { uniq } from 'ramda'
import { KeyboardEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TraitDataGrid } from '../TraitDataGrid'
import { useTraitColumns } from '../useTraitColumns'
import { TemplateEditorTableCell } from './TemplateEditorTableCell'

interface TemplateEditorMainProps {
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName; traits: TaskDataTraitStatus[] }>
  draftDetails: PracticeTemplateDetail[]
  years: string[]
  onValueSave: (
    traitId: string,
    year: number,
    filledValues: { value?: unknown; input?: unknown; result?: unknown }
  ) => void
  onValueDelete: (traitId: string, year: number) => void
  onAddPractice: (traitId: string) => void
  onPrePopulate: () => void
  availableTraitIds: string[]
  fieldHasData: boolean
  canEdit: boolean
  canChangeOwnerScope: boolean
  canDelete: boolean
  isDeleting: boolean
  isSaving: boolean
  isDirty: boolean
  draftName: string
  nameError: string | null
  selectedId: string | 'new'
  onDraftNameChange: (name: string) => void
  onConfirmName: () => boolean
  onDelete: () => void
  onClose: () => void
  onSave: () => void
  ownerScope: 'farmer' | 'all'
  onOwnerScopeChange: (scope: 'farmer' | 'all') => void
  shouldFocusName?: boolean
  onShouldFocusNameHandled?: () => void
}

export const TemplateEditorMain = ({
  task,
  draftDetails,
  years,
  onValueSave,
  onValueDelete,
  onAddPractice,
  onPrePopulate,
  availableTraitIds,
  fieldHasData,
  canEdit,
  canChangeOwnerScope,
  canDelete,
  isDeleting,
  isSaving,
  isDirty,
  draftName,
  nameError,
  selectedId,
  onDraftNameChange,
  onConfirmName,
  onDelete,
  onClose,
  onSave,
  ownerScope,
  onOwnerScopeChange,
  shouldFocusName,
  onShouldFocusNameHandled,
}: TemplateEditorMainProps) => {
  const { t } = useTranslation([
    'traits',
    '@cibo/programs/TemplateEditorModal',
    '@cibo/programs/TaskEditorTable',
  ])
  const [addPracticeAnchorEl, setAddPracticeAnchorEl] = useState<HTMLElement | null>(null)
  const [_forceRender, setForceRender] = useState(0)
  const [isNameEditing, setIsNameEditing] = useState(selectedId === 'new')
  const [pendingFocus, setPendingFocus] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setIsNameEditing(selectedId === 'new')
  }, [selectedId])

  useEffect(() => {
    if (shouldFocusName) {
      setIsNameEditing(true)
      setPendingFocus(true)
      onShouldFocusNameHandled?.()
    }
  }, [shouldFocusName])

  useEffect(() => {
    if (pendingFocus && nameInputRef.current) {
      nameInputRef.current.focus()
      setPendingFocus(false)
    }
  })

  const hasPractices = draftDetails.length > 0
  const showPrePopulate = canEdit && !hasPractices && fieldHasData

  const handleNameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (onConfirmName()) setIsNameEditing(false)
    } else if (e.key === 'Escape') {
      if (selectedId !== 'new') setIsNameEditing(false)
    }
  }

  const handleNameConfirm = () => {
    if (onConfirmName()) setIsNameEditing(false)
  }

  const showFooter = canEdit || canDelete || selectedId === 'new'

  // Force grid layout recalculation on mount and when practices change
  // Without this, the header columns do not take the pinned column into account
  useEffect(() => {
    const timer = setTimeout(() => {
      // Dispatch resize event to trigger DataGrid's pinned column recalculation
      window.dispatchEvent(new Event('resize'))
      // Force a re-render to ensure header alignment
      setForceRender(prev => prev + 1)
    }, 50)
    return () => clearTimeout(timer)
  }, [hasPractices, years.length])

  const rows = useMemo(() => {
    const traitIds = uniq(draftDetails.map(d => d.traitId))
    return traitIds.map(traitId => {
      const row: { id: string; traitId: string; [key: string]: any } = { id: traitId, traitId }
      years.forEach(year => {
        const d = draftDetails.find(d => d.traitId === traitId && d.year === Number(year))
        const cellValue: Record<string, unknown> = {}
        if (d?.input !== undefined) cellValue.input = d.input
        if (d?.result !== undefined) cellValue.result = d.result
        if (d?.value !== undefined) cellValue.value = d.value
        row[year] = Object.keys(cellValue).length ? cellValue : undefined
      })
      return row
    })
  }, [draftDetails, years])

  const renderYearCell = useCallback(
    (_year: string) => (params: GridRenderCellParams) =>
      (
        <TemplateEditorTableCell
          task={task}
          canEdit={canEdit}
          onValueSave={onValueSave}
          onValueDelete={onValueDelete}
          {...params}
        />
      ),
    [task, canEdit, onValueSave, onValueDelete]
  )

  const columns = useTraitColumns({
    traitIdHeaderName: t('practice', { ns: '@cibo/programs/TemplateEditorModal' }),
    years,
    renderYearCell,
  })

  return (
    <Stack sx={{ flex: 1, overflow: 'hidden' }}>
      {/* Header: name + close */}
      <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 1, minHeight: 48 }}>
        {isNameEditing ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ width: '50%', minWidth: 0 }}
          >
            <TextField
              autoFocus
              inputRef={nameInputRef}
              size="small"
              label={t('templateName', { ns: '@cibo/programs/TemplateEditorModal' })}
              value={draftName}
              onChange={e => onDraftNameChange(e.target.value)}
              onKeyDown={handleNameKeyDown}
              error={!!nameError}
              helperText={nameError ?? undefined}
              disabled={!canEdit}
              sx={{ flex: 1 }}
              slotProps={{ htmlInput: { maxLength: 100 } }}
              data-testid="template-name-input"
            />
            <Button
              size="small"
              onClick={handleNameConfirm}
              data-testid="template-name-confirm-button"
            >
              {t('save', { ns: '@cibo/programs/TemplateEditorModal' })}
            </Button>
          </Stack>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ width: '50%', minWidth: 0 }}
          >
            <Typography
              data-testid="template-name-display"
              sx={{
                fontSize: 16,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {draftName || t('newTemplate', { ns: '@cibo/programs/TemplateEditorModal' })}
            </Typography>
            {canEdit && (
              <IconButton
                size="small"
                onClick={() => setIsNameEditing(true)}
                aria-label={t('editTemplateName', { ns: '@cibo/programs/TemplateEditorModal' })}
                data-testid="template-name-edit-button"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        )}
        <Box sx={{ flex: 1 }} />
        <IconButton
          size="small"
          onClick={onClose}
          aria-label={t('close', { ns: '@cibo/programs/TemplateEditorModal' })}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <Divider />

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {hasPractices ? (
          <TraitDataGrid rows={rows} columns={columns} hideFooter />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              minHeight: 160,
              m: 2,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('emptyState', { ns: '@cibo/programs/TemplateEditorModal' })}
            </Typography>
            {showPrePopulate && (
              <Button
                variant="outlined"
                size="small"
                data-testid="template-pre-populate-button"
                onClick={onPrePopulate}
              >
                {t('prePopulate', { ns: '@cibo/programs/TemplateEditorModal' })}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {showFooter && (
        <>
          <Divider />
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5 }}>
            {canEdit && (
              <>
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  data-testid="template-add-practice-button"
                  disabled={availableTraitIds.length === 0}
                  onClick={(e: MouseEvent<HTMLButtonElement>) =>
                    setAddPracticeAnchorEl(e.currentTarget)
                  }
                >
                  {t('addPractice', { ns: '@cibo/programs/TemplateEditorModal' })}
                </Button>
                {canChangeOwnerScope && (
                  <Select
                    size="small"
                    data-testid="template-owner-scope-select"
                    value={ownerScope}
                    onChange={e => onOwnerScopeChange(e.target.value as 'farmer' | 'all')}
                    renderValue={value => (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {value === 'farmer' ? (
                          <PersonIcon fontSize="small" />
                        ) : (
                          <PeopleIcon fontSize="small" />
                        )}
                        <Typography variant="body2">
                          {t(value === 'farmer' ? 'onlyThisFarmer' : 'allFarmers', {
                            ns: '@cibo/programs/TemplateEditorModal',
                          })}
                        </Typography>
                      </Stack>
                    )}
                  >
                    <MenuItem value="farmer">
                      <ListItemIcon>
                        <PersonIcon fontSize="small" />
                      </ListItemIcon>
                      {t('onlyThisFarmer', { ns: '@cibo/programs/TemplateEditorModal' })}
                    </MenuItem>
                    <MenuItem value="all">
                      <ListItemIcon>
                        <PeopleIcon fontSize="small" />
                      </ListItemIcon>
                      {t('allFarmers', { ns: '@cibo/programs/TemplateEditorModal' })}
                    </MenuItem>
                  </Select>
                )}
              </>
            )}

            <Box sx={{ flex: 1 }} />

            <Button
              variant="outlined"
              color="error"
              data-testid="template-delete-button"
              loading={isDeleting}
              disabled={isDeleting || selectedId === 'new'}
              onClick={onDelete}
            >
              {t('deleteTemplate', { ns: '@cibo/programs/TemplateEditorModal' })}
            </Button>

            <Button
              variant="contained"
              data-testid="template-save-button"
              loading={isSaving}
              disabled={!isDirty || isSaving || !hasPractices || !draftName.trim()}
              onClick={onSave}
            >
              {t('saveTemplate', { ns: '@cibo/programs/TemplateEditorModal' })}
            </Button>
          </Stack>
        </>
      )}

      {/* Add practice dropdown */}
      <Menu
        anchorEl={addPracticeAnchorEl}
        open={!!addPracticeAnchorEl}
        onClose={() => setAddPracticeAnchorEl(null)}
      >
        {availableTraitIds.length === 0 ? (
          <MenuItem disabled>
            {t('allPracticesAdded', { ns: '@cibo/programs/TemplateEditorModal' })}
          </MenuItem>
        ) : (
          availableTraitIds.map(traitId => (
            <MenuItem
              key={traitId}
              onClick={() => {
                onAddPractice(traitId)
                setAddPracticeAnchorEl(null)
              }}
            >
              {t(traitId, { ns: 'traits' })}
            </MenuItem>
          ))
        )}
      </Menu>
    </Stack>
  )
}
